import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
      // For testing - in production remove this
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      toast.success('Reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        {!sent ? (
          <>
            <div className="mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-zinc-100 font-['Manrope']">Forgot password?</h1>
              <p className="text-zinc-400 mt-2">Enter your email and we'll send you reset instructions.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 focus:ring-violet-500/20 h-12"
                />
              </div>

              <Button
                type="submit"
                data-testid="forgot-submit-btn"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-medium rounded-xl"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-100 font-['Manrope'] mb-4">Check your email</h1>
            <p className="text-zinc-400 mb-6">
              We've sent password reset instructions to <strong className="text-zinc-200">{email}</strong>
            </p>
            
            {/* For testing - remove in production */}
            {resetToken && (
              <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 mb-6 text-left">
                <p className="text-xs text-zinc-500 mb-2">Testing: Reset Token</p>
                <Link 
                  to={`/reset-password?token=${resetToken}`}
                  className="text-violet-400 hover:text-violet-300 text-sm break-all"
                >
                  Click here to reset password
                </Link>
              </div>
            )}

            <Link to="/login">
              <Button variant="outline" className="border-zinc-700 text-zinc-300">
                Back to login
              </Button>
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
